import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import { NumberSignEnum } from '../../../lib/enums';
import { BaseSymbolData, Week52HighLowResponse } from '../../../lib/models/fundamental/types';
import { RootState } from '../../../lib/store';
import TradePriceCache from '../../../lib/store-util/TradePriceCache';
import { formatCurrency, getLimitedText } from '../../../lib/util/DataHelpers';
import { calculatePriceMovement, roundToCurrencyFormat } from '../../../lib/util/lastPriceChangeHelpers';
import { getSymbolFirstPart } from '../../../lib/util/MarketDataHelpers';
import { INVALID_VALUE, PriceMovement } from '../../../lib/util/types';

import { ChartLine, CompanyLogo } from '../../../components';
import { useIsMobile } from '../../../hooks';
import { UI_ROUTES } from '../../../util/routes';

import styles from './PopularGroupTable.module.scss';

type Props = {
    item: BaseSymbolData | Week52HighLowResponse;
    isWeek52Item?: boolean;
}

let priceMovement: PriceMovement;

function getCompanyName(item: BaseSymbolData | Week52HighLowResponse): string | undefined {
  return 'companyName' in item ? item.companyName : (item as Week52HighLowResponse).name;
}

const PopularGroupTableItem = ({ item, isWeek52Item }: Props) => {
  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const companyName = getCompanyName(item);
  const companyLogosAndNames = useSelector((state: RootState) => state.fundamental.companyLogoAndName);
  const itemUrl = useMemo(
    () => item.url || companyLogosAndNames[item.symbol]?.url,
    [item.url, companyLogosAndNames, item.symbol],
  );
  const {
    direction, change, changePercentWithSign, currentPrice,
  } = TradePriceCache.use(getSymbolFirstPart(item.symbol), 'PopularGroupTableItem');
  const nameLimit = isMobile ? 16 : 20;

  priceMovement = calculatePriceMovement(currentPrice, item.symbol);

  function handleOnClick() {
    navigate(`${UI_ROUTES.symbol}/${item.symbol}`);
  }

  const getChangePercentageByItemType = ():JSX.Element => {
    const content: JSX.Element[] = [
      <div
        key={item.symbol}
        className={classNames(styles.columnPercentChange, { [styles.columnPercentChangeMob]: isMobile })}
      >
        {changePercentWithSign ? (
          <span className={
            direction === NumberSignEnum.Negative ? styles.priceTextMinus : styles.priceTextPlus
          }
          >
            {changePercentWithSign}
          </span>
        ) : (
          INVALID_VALUE
        )}
      </div>,
    ];

    if (isWeek52Item) {
      const item52Week = item as Week52HighLowResponse;
      content.push(
        <div className={classNames(styles.columnPercentChange, { [styles.columnPercentChangeMob]: isMobile })}>
          <span className={item52Week.changePercent > 0 ? styles.priceTextPlus : styles.priceTextMinus}>
            {item52Week.changePercent > 0 && NumberSignEnum.Positive}
            {item52Week.changePercent.toFixed(2)}
            %
          </span>
        </div>,
      );
    }

    return <>{content}</>;
  };

  return (
    <tr className={classNames(styles.wrapperRow, { [styles.row]: true })} onClick={handleOnClick}>
      {
            isMobile ? (
              <>
                <td
                  className={
                      classNames(
                        styles.columnSymbolName,
                        styles.columnSymbolNameWidthMobile,
                      )
                    }
                >
                  <div className={styles.wrapperContent}>
                    <div className={styles.logo}>
                      <CompanyLogo
                        url={itemUrl}
                        className={styles.logo}
                        symbol={item.symbol}
                      />
                    </div>
                    <div className={styles.companyInfo}>
                      <span className={styles.symbol}>{item.symbol}</span>
                      <span className={styles.companyName}>
                        {companyName && companyName.length > 12
                          ? `${companyName.slice(0, 12)}...`
                          : companyName}
                      </span>
                    </div>
                  </div>
                </td>
                {isWeek52Item ? (
                  <td>
                    <div>
                      {formatCurrency(currentPrice)}
                    </div>
                    <div className={styles.emptyRow} />
                  </td>
                ) : (
                  <td className={styles.columnChartMobile}><ChartLine symbol={item.symbol} /></td>
                )}
                <td className={styles.columnLastPriceMobile}>
                  {!isWeek52Item && (
                    <div className={styles.currentPrice}>
                      {formatCurrency(roundToCurrencyFormat(currentPrice || 0))}
                    </div>
                  )}
                  {getChangePercentageByItemType()}
                </td>
              </>
            ) : (
              <>
                <td className={styles.columnSymbolName}>
                  <div className={isWeek52Item ? styles.wrapperContent52Week : styles.wrapperContent}>
                    <div className={styles.logoWrapper}>
                      <CompanyLogo url={itemUrl} logo={companyLogosAndNames[item.symbol]?.logo || ''} symbol={item.symbol} />
                    </div>
                    <div className={styles.companyInfo}>
                      <span className={styles.symbol}>{item.symbol}</span>
                      <span className={styles.companyName}>
                        {getLimitedText(companyName, nameLimit)}
                      </span>
                    </div>
                  </div>
                </td>
                <td className={styles.columnChart}><ChartLine symbol={item.symbol} /></td>
                <td className={styles.columnLastPrice}>
                  <span className={classNames(styles.currentPrice, {
                    [styles.noPriceChange]: priceMovement === 'equal',
                    [styles.negativeChange]: priceMovement === 'down',
                    [styles.positiveChange]: priceMovement === 'up',
                  })}
                  >
                    {formatCurrency(currentPrice)}
                  </span>
                </td>
                <td className={styles.columnChange}>
                  {change
                    ? (
                      <span className={direction === '-' ? styles.priceTextMinus : styles.priceTextPlus}>
                        {isWeek52Item ? changePercentWithSign : formatCurrency(change)}
                      </span>
                    )
                    : INVALID_VALUE }
                </td>
                <td className={styles.columnPercentChange}>
                  {changePercentWithSign
                    ? (
                      <span className={direction === '-' ? styles.priceTextMinus : styles.priceTextPlus}>
                        {isWeek52Item ? `${direction === '-' ? '' : '+'}${(item as Week52HighLowResponse).changePercent.toFixed(2)}%` : changePercentWithSign}
                      </span>
                    )
                    : INVALID_VALUE}
                </td>
              </>
            )
        }
    </tr>
  );
};

export default PopularGroupTableItem;
